import {
  IScheduleHandler,
  ScheduleContext,
  ScheduleEvaluationResult,
} from "../types/index";
import {
  Playlist,
  ScheduleType,
  TimeWindowSchedule,
  isTimeWindowSchedule,
} from "../types";

/**
 * Time Window Schedule Handler
 *
 * Handles playlists with ScheduleType.TimeWindow (1)
 * Active during specific time ranges on selected days of the week.
 *
 * Example: Monday-Friday 9:00 AM - 5:00 PM
 */
export class TimeWindowScheduleHandler implements IScheduleHandler {
  public readonly scheduleType = ScheduleType.TimeWindow;

  /**
   * Evaluate if playlist is active based on current day and time
   */
  evaluate(
    playlist: Playlist,
    context: ScheduleContext
  ): ScheduleEvaluationResult {
    const schedule = playlist.schedule;

    if (!schedule || !isTimeWindowSchedule(schedule)) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: "Invalid or missing TimeWindow schedule configuration",
      };
    }

    const currentDay = context.currentTime.getDay(); // 0=Sunday
    const currentTime = this.getTimeString(context.currentTime);

    // Check if current day is in the schedule
    const isDayActive = schedule.daysOfWeek.includes(currentDay);

    if (!isDayActive) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: `Not active on ${this.getDayName(currentDay)}`,
        nextEvaluationTime: this.getNextActiveDay(
          schedule,
          context.currentTime
        ),
      };
    }

    // Check if current time is within the window
    const isTimeActive =
      currentTime >= schedule.startTime && currentTime <= schedule.endTime;

    if (!isTimeActive) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: `Outside time window (${schedule.startTime} - ${schedule.endTime})`,
        nextEvaluationTime: this.getNextWindowTime(
          schedule,
          context.currentTime
        ),
      };
    }

    // Calculate deactivation time
    const deactivationTime = this.parseTimeToDate(
      schedule.endTime,
      context.currentTime
    );

    return {
      isActive: true,
      effectivePriority: playlist.priority,
      deactivationTime,
      reason: `Active on ${this.getDayName(currentDay)} during ${
        schedule.startTime
      } - ${schedule.endTime}`,
      nextEvaluationTime: deactivationTime,
    };
  }

  /**
   * Get next state change time
   */
  getNextStateChange(
    playlist: Playlist,
    context: ScheduleContext
  ): Date | null {
    const schedule = playlist.schedule;

    if (!schedule || !isTimeWindowSchedule(schedule)) {
      return null;
    }

    const currentDay = context.currentTime.getDay();
    const currentTime = this.getTimeString(context.currentTime);

    // If currently in active day
    if (schedule.daysOfWeek.includes(currentDay)) {
      // If before start time, return start time today
      if (currentTime < schedule.startTime) {
        return this.parseTimeToDate(schedule.startTime, context.currentTime);
      }

      // If in window, return end time today
      if (
        currentTime >= schedule.startTime &&
        currentTime <= schedule.endTime
      ) {
        return this.parseTimeToDate(schedule.endTime, context.currentTime);
      }

      // If after end time, return next active day start time
      return this.getNextActiveDay(schedule, context.currentTime);
    }

    // Not an active day, return next active day
    return this.getNextActiveDay(schedule, context.currentTime);
  }

  /**
   * Validate TimeWindow schedule configuration
   */
  public validateConfiguration(playlist: Playlist): string[] {
    const errors: string[] = [];
    const schedule = playlist.schedule;

    if (!schedule || !isTimeWindowSchedule(schedule)) {
      errors.push("TimeWindow schedule configuration is missing or invalid");
      return errors;
    }

    if (!schedule.startTime) {
      errors.push("Start time is required for TimeWindow schedules");
    }

    if (!schedule.endTime) {
      errors.push("End time is required for TimeWindow schedules");
    }

    if (!schedule.daysOfWeek || schedule.daysOfWeek.length === 0) {
      errors.push(
        "At least one day of week is required for TimeWindow schedules"
      );
    }

    return errors;
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  private getTimeString(date: Date): string {
    return date.toTimeString().split(" ")[0]; // "HH:MM:SS"
  }

  private parseTimeToDate(timeStr: string, baseDate: Date): Date {
    const [hours, minutes, seconds] = timeStr.split(":").map(Number);
    const result = new Date(baseDate);
    result.setHours(hours, minutes, seconds || 0, 0);
    return result;
  }

  private getNextActiveDay(
    schedule: TimeWindowSchedule,
    currentTime: Date
  ): Date {
    const currentDay = currentTime.getDay();
    const sortedDays = [...schedule.daysOfWeek].sort((a, b) => a - b);

    // Find next day in schedule
    let nextDay = sortedDays.find((day) => day > currentDay);

    // If no day found after current day, wrap to first day next week
    if (nextDay === undefined) {
      nextDay = sortedDays[0];
    }

    // Calculate days until next active day
    let daysUntil = nextDay - currentDay;
    if (daysUntil <= 0) {
      daysUntil += 7;
    }

    // Create date for next active day at start time
    const nextDate = new Date(currentTime);
    nextDate.setDate(nextDate.getDate() + daysUntil);

    const [hours, minutes, seconds] = schedule.startTime.split(":").map(Number);
    nextDate.setHours(hours, minutes, seconds || 0, 0);

    return nextDate;
  }

  private getNextWindowTime(
    schedule: TimeWindowSchedule,
    currentTime: Date
  ): Date {
    const currentDay = currentTime.getDay();
    const currentTimeStr = this.getTimeString(currentTime);

    // If on active day but before window, return start time today
    if (
      schedule.daysOfWeek.includes(currentDay) &&
      currentTimeStr < schedule.startTime
    ) {
      return this.parseTimeToDate(schedule.startTime, currentTime);
    }

    // Otherwise return next active day
    return this.getNextActiveDay(schedule, currentTime);
  }

  private isValidTimeFormat(timeStr: string): boolean {
    if (!timeStr) return false;
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/;
    return timeRegex.test(timeStr);
  }

  private getDayName(day: number): string {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    return days[day] || "Unknown";
  }
}
